// M4 world §10 — megaprojects 2000–2029 (rules.MEGAPROJECTS_M4, MEGAPROJECT_RULES_M4).
// Buy a share (shares[0]–shares[1]) from FID until first oil. At joining, the project's outcome is drawn from the seed:
//   overrun  ~ lognormal(0, overrunSigma)  (0.1 Liza … 0.6 Kashagan, 0.8 TMX; default 0.35)
//   delay    ~ N(0, 270 d × overrunSigma / 0.35), the early side compressed ×0.3 (megaprojects seldom beat schedule)
// Capex = costB × overrun × share, paid over the build years by capexCurve (15/30/30/25 %), the rest at first oil.
// First oil = firstOil + delay — unless a news phase overrides it (history wins): 'open' opens it that day, 'hold'
// stops the clock until the next 'start'/'open', 'shut' stops production (Kashagan's cracked pipelines: until Sept
// 2016), 'cancel' ends it (refund cancelRefundShare of what was paid). Producing projects pay the member its share of
// output × netback (oil) or tariff/tolling income (pipelines, LNG). Pipeline projects gate their firm transports
// ('megaproject:<id>' — m3 megaprojects.openDayOf hunk).
import { dateToDay, yearFrac } from '../../core/time.js';
import { fieldDef, yf } from '../defs.js';
import { inflate } from '../../data/cpi.js';
import { MEGAPROJECTS_M4, MEGAPROJECT_RULES_M4 } from '../../data/m4/rules.js';
import { spotPrice } from '../../economy/market.js';
import { spend, earn, notify, fmt$ } from '../util.js';
import { W, peekW, m4On, hashNormal } from './worldCore.js';

const R = MEGAPROJECT_RULES_M4;
export const NETBACK_SHARE = 0.4;            // member's netback per barrel as a share of Brent after government take & opex (est.)
export const PIPELINE_TARIFF = 2.5;          // $/bbl (2015 $) tariff income on a crude line (est.)
export const PIPELINE_FILL = 0.7;
export const LNG_TOLL_PER_MMBTU = 2.5;       // $/MMBtu (2016 $) liquefaction toll (est.)
export const MMBTU_PER_MT = 52e6;
export const RAMP_DAYS = 365;
export const PLATEAU_YEARS = 5;
export const DECLINE_AFTER = 0.08;
export const EARLY_COMPRESS = 0.3;
const SHUT_UNTIL = { kashagan_phase1: dateToDay(2016, 9, 28) };
const mpDay = (y) => (y == null ? null : Math.round(dateToDay(Math.floor(y), 1, 1) + (y - Math.floor(y)) * 365.25));
export const fidDay = (mp) => mpDay(mp.fid);
export const histFirstOilDay = (mp) => mpDay(mp.firstOil);
export const cancelDay = (mp) => mpDay(mp.cancelled);

// Seeded outcome for a project (pure in (seed, id)): overrun multiple and schedule delay (days).
export function megaOutcome(state, id, seedKey = '') {
  const mp = MEGAPROJECTS_M4[id];
  const sig = mp.overrunSigma ?? R.overrunSigma;
  const overrun = Math.exp(sig * hashNormal(state, `mega:${id}:o${seedKey}`));
  const z = hashNormal(state, `mega:${id}:d${seedKey}`);
  const sd = R.delaySigmaDays * sig / R.overrunSigma;
  const delayDays = Math.round(z >= 0 ? z * sd : z * sd * EARLY_COMPRESS);
  return { overrun: Math.round(overrun * 1000) / 1000, delayDays };
}

const news = (state, id) => peekW(state)?.megaNews?.[id] ?? null;

// Phase & first-oil day for project `id` today (with or without a member share).
export function megaStatus(state, id) {
  const mp = MEGAPROJECTS_M4[id];
  if (!mp) return null;
  const s = peekW(state)?.mega?.[id] ?? null;
  const n = news(state, id);
  const d = state.day;
  const fid = fidDay(mp);
  const cancel = cancelDay(mp);
  if ((cancel != null && d >= cancel) || n?.phase === 'cancel') return { phase: 'cancelled', firstOilDay: null, fidDay: fid };
  let first = n?.openDay ?? (s?.firstOilDay ?? (mp.firstOil != null ? histFirstOilDay(mp) : null));
  if (n?.phase === 'hold') {
    // after opening: stopped until the next 'open'; before: on hold until a restart or the first-oil date passes
    if ((n.openDay != null && n.day >= n.openDay) || first == null || d < first) return { phase: 'hold', firstOilDay: n.openDay != null ? first : first, fidDay: fid, since: n.day };
  }
  if (n?.phase === 'shut') {
    const until = n.shutUntil ?? SHUT_UNTIL[id] ?? null;
    if (until == null || d < until) return { phase: 'shut', firstOilDay: first, fidDay: fid, since: n.day, untilDay: until };
  }
  if (first == null) return { phase: fid != null && d >= fid ? 'construction' : 'planned', firstOilDay: null, fidDay: fid };
  if (d >= first) return { phase: 'producing', firstOilDay: first, fidDay: fid };
  if (fid != null && d < fid) return { phase: 'planned', firstOilDay: first, fidDay: fid };
  return { phase: 'construction', firstOilDay: first, fidDay: fid };
}
export const megaOpen = (state, id) => ['producing'].includes(megaStatus(state, id)?.phase);

// m3 megaprojects.openDayOf hunk: the day an M4 pipeline/LNG project's transport opens (Infinity while not open).
export function m4MegaOpenDay(state, id) {
  if (!MEGAPROJECTS_M4[id]) return null;
  const st = megaStatus(state, id);
  return st.phase === 'producing' ? st.firstOilDay : Infinity;
}
export const m4MegaName = (id) => (MEGAPROJECTS_M4[id] ? megaName(id) : null);
const NAMES = { thunder_horse: 'Thunder Horse', perdido: 'Perdido', jack_st_malo: 'Jack/St. Malo', lula_pilot: 'Lula pilot', buzios_fpsos: 'Búzios FPSOs', girassol: 'Girassol', kaombo: 'Kaombo',
  bonga: 'Bonga', egina: 'Egina', liza_phase1: 'Liza Phase 1', kashagan_phase1: 'Kashagan Phase 1', prirazlomnaya: 'Prirazlomnaya', shtokman: 'Shtokman', sabine_pass: 'Sabine Pass LNG',
  dapl: 'Dakota Access Pipeline', permian_pipes_2019: 'Permian long-haul pipelines (Cactus II, Gray Oak, EPIC)', seaway_reversal: 'Seaway reversal', keystone_xl: 'Keystone XL', tmx: 'Trans Mountain Expansion', mvp: 'Mountain Valley Pipeline' };
export const megaName = (id) => NAMES[id] ?? id.replace(/_/g, ' ');

// news effect { type: 'megaprojectM4', id, phase }
export function applyMegaNews(state, id, phase, day = state.day, { silent = false } = {}) {
  const mp = MEGAPROJECTS_M4[id];
  if (!mp || !m4On(state)) return null;
  const Wm = W(state);
  const prev = Wm.megaNews[id] ?? {};
  const rec = { ...prev, phase, day };
  if (phase === 'open') { rec.openDay = day; rec.shutUntil = null; }
  if (phase === 'start' || phase === 'fid') rec.phase = null;
  Wm.megaNews[id] = rec;
  const s = Wm.mega[id];
  if (s?.share > 0 && !silent) {
    if (phase === 'cancel') cancelShare(state, id, s);
    else notify(state, 'megaproject', { id, phase, member: true, press: phase === 'open' ? 'megaprojectOpen' : null, pause: phase !== 'start', level: phase === 'open' ? 'major' : 'warn',
      text: { hold: `${megaName(id)} is on hold — your capital is tied up until it restarts.`, shut: `${megaName(id)} is shut in: production stops until repairs are done.`, open: `${megaName(id)}: first oil! Your ${Math.round(s.share * 1000) / 10}% starts paying.`, start: `${megaName(id)}: work resumes.`, fid: `${megaName(id)} sanctioned.` }[phase] ?? `${megaName(id)}: ${phase}.` });
  }
  return rec;
}

function cancelShare(state, id, s) {
  if (s.cancelled) return;
  s.cancelled = state.day;
  const refund = s.paid * R.cancelRefundShare;
  if (refund > 0) earn(state, refund, 'recoveries');
  s.refund = Math.round(refund);
  notify(state, 'megaproject', { id, phase: 'cancel', member: true, pause: true, level: 'major', text: `${megaName(id)} is cancelled. You recover ${fmt$(refund)} of the ${fmt$(s.paid)} you paid in.` });
}

// Quote for joining at `share` (era $).
export function megaQuote(state, id, share) {
  const mp = MEGAPROJECTS_M4[id];
  if (!mp) return { ok: false, reason: 'No such project.' };
  const st = megaStatus(state, id);
  const [lo, hi] = mp.shares ?? [0, 0];
  const y = Math.min(yf(state), 2025);
  const fidY = mp.fid ?? y;
  const total = inflate(mp.costB * 1e9, Math.floor(Math.max(2000, fidY)), y);
  const reason = !m4On(state) ? 'Not before 2000.' : !(hi > 0) ? `${megaName(id)} is not open to outside partners.` : st.phase === 'cancelled' ? 'Cancelled.'
    : st.phase === 'producing' ? 'Already producing — shares are no longer offered.' : st.phase === 'planned' && mp.fid != null ? `Partners are invited at FID (${Math.floor(mp.fid)}).`
      : mp.fid == null && st.phase === 'planned' ? 'The project never reached FID.' : null;
  const sh = Math.round(Number(share) * 1000) / 1000;
  const shareOk = sh >= lo && sh <= hi;
  return { ok: !reason && shareOk, reason: reason ?? (shareOk ? null : `Shares run ${lo * 100}–${hi * 100}%.`), id, share: sh, minShare: lo, maxShare: hi, totalCost: Math.round(total), yourCost: Math.round(total * sh), phase: st.phase };
}

export function buyMegaShareImpl(state, id, share) {
  const q = megaQuote(state, id, share);
  if (!q.ok) return { ok: false, msg: q.reason };
  const Wm = W(state);
  if (Wm.mega[id]?.share > 0) return { ok: false, msg: `You already hold ${Math.round(Wm.mega[id].share * 1000) / 10}% of ${megaName(id)}.` };
  const mp = MEGAPROJECTS_M4[id];
  const o = megaOutcome(state, id);
  const committed = q.yourCost * o.overrun;
  const firstHist = histFirstOilDay(mp);
  const firstOilDay = firstHist != null ? Math.max(state.day + 30, firstHist + o.delayDays) : null;
  const upfront = committed * R.capexCurve[0];
  if (state.company.cash < upfront) return { ok: false, msg: `The first cash call is ${fmt$(upfront)}.` };
  Wm.mega[id] = { id, share: q.share, joinDay: state.day, committed: Math.round(committed), paid: 0, overrun: o.overrun, delayDays: o.delayDays, firstOilDay, income: 0, bbl: 0, cancelled: null };
  payCapex(state, Wm.mega[id], upfront);
  notify(state, 'megaproject', { id, phase: 'joined', share: q.share, member: true, text: `Bought ${Math.round(q.share * 1000) / 10}% of ${megaName(id)}: ${fmt$(committed)} in cash calls through first oil.` });
  return { ok: true, msg: `Joined ${megaName(id)} with ${Math.round(q.share * 1000) / 10}%. Budget ${fmt$(q.yourCost)} (your share), cash calls over the build.` };
}
function payCapex(state, s, amt) { if (amt > 0) { spend(state, amt, 'capex', false); s.paid += amt; } }

// Daily: cash calls (by the capex curve over the build), income once producing.
export function tickMegaprojectsM4(state) {
  const Wm = peekW(state);
  if (!Wm?.mega) return;
  const y = Math.min(yf(state), 2025);
  const S = Math.max(0, spotPrice(state));
  for (const [id, s] of Object.entries(Wm.mega)) {
    if (!(s.share > 0) || s.cancelled) continue;
    const mp = MEGAPROJECTS_M4[id];
    const st = megaStatus(state, id);
    if (st.phase === 'cancelled') { cancelShare(state, id, s); continue; }
    if (st.phase === 'hold') continue;
    const first = st.firstOilDay ?? s.firstOilDay;
    if (st.phase === 'construction' || (first != null && state.day < first)) {
      // capex curve: by elapsed share of the build, the cumulative share of committed spend is due
      const start = s.joinDay;
      const end = first ?? (start + 4 * 365);
      const t = Math.max(0, Math.min(1, (state.day - start) / Math.max(1, end - start)));
      const years = R.capexCurve.length;
      const k = Math.min(years - 1, Math.floor(t * years));
      const due = s.committed * (R.capexCurve.slice(0, k).reduce((a, b) => a + b, 0) + R.capexCurve[k] * (t * years - k));
      payCapex(state, s, Math.max(0, Math.min(s.committed - s.paid, due - s.paid)));
      continue;
    }
    if (s.paid < s.committed) payCapex(state, s, s.committed - s.paid); // the balance at first oil
    if (st.phase !== 'producing') continue;
    const age = state.day - first;
    const ramp = Math.min(1, age / RAMP_DAYS);
    const yrs = age / 365.25;
    const decline = yrs > PLATEAU_YEARS ? Math.exp(-DECLINE_AFTER * (yrs - PLATEAU_YEARS)) : 1;
    let inc = 0, bbl = 0;
    if (mp.product === 'lng') inc = s.share * (mp.capacityMmtpa ?? 0) * MMBTU_PER_MT / 365 * 0.85 * ramp * inflate(LNG_TOLL_PER_MMBTU, 2016, y);
    else if (mp.fieldId == null) inc = s.share * (mp.capacityKbd ?? (mp.capacityMcfd ?? 0) / 6000) * 1000 * PIPELINE_FILL * ramp * inflate(PIPELINE_TARIFF, 2015, y);
    else { bbl = s.share * (mp.capacityKbd ?? 0) * 1000 * ramp * decline; inc = bbl * S * NETBACK_SHARE; }
    if (inc > 0) { earn(state, inc, 'revenue'); s.income += inc; s.bbl += bbl; state.company.stats.revenueTotal = (state.company.stats.revenueTotal ?? 0) + inc; }
  }
}

// ---- view ---------------------------------------------------------------------------------------------------------------
export function megaprojectsM4View(state) {
  const Wm = peekW(state);
  return Object.entries(MEGAPROJECTS_M4).map(([id, mp]) => {
    const st = m4On(state) ? megaStatus(state, id) : { phase: 'planned', firstOilDay: histFirstOilDay(mp) };
    const s = Wm?.mega?.[id] ?? null;
    const q = m4On(state) ? megaQuote(state, id, mp.shares?.[0] ?? 0) : null;
    return { id, name: megaName(id), fieldId: mp.fieldId, fieldName: mp.fieldId ? fieldDef(mp.fieldId)?.name ?? mp.fieldId : null, kind: mp.product === 'lng' ? 'lng' : mp.fieldId ? 'field' : 'pipeline',
      phase: st.phase, fidYear: mp.fid ?? null, historicalFirstOilYear: mp.firstOil ?? null, firstOilDay: st.firstOilDay ?? null, firstOilYear: st.firstOilDay != null ? Math.round(yearFrac(st.firstOilDay) * 100) / 100 : null,
      costB: mp.costB, capacityKbd: mp.capacityKbd ?? null, minShare: mp.shares?.[0] ?? 0, maxShare: mp.shares?.[1] ?? 0, overrunSigma: mp.overrunSigma ?? R.overrunSigma, historical: mp.historical ?? null,
      canJoin: !!q && !q.reason && !(s?.share > 0), joinReason: q?.reason ?? null, totalCostNow: q?.totalCost ?? null,
      share: s?.share ?? 0, committed: s?.committed ?? 0, paid: Math.round(s?.paid ?? 0), income: Math.round(s?.income ?? 0), overrun: s?.overrun ?? null, delayDays: s?.delayDays ?? null, cancelled: s?.cancelled ?? null,
      news: news(state, id) ? { ...news(state, id) } : null };
  });
}
